/**
 * Portfolio X-Ray Component
 *
 * Provides detailed holdings analysis with two modes:
 * - Table View: Custom column selection for holdings data
 * - Contributions View: Field or exposure contribution breakdown
 */

import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTable,
  faChartPie,
  faCopy,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { Button } from "@/components/ui/button/Button";
import { useNotification } from "@/context/NotificationContext";
import { XRayTableView } from "./XRayTableView";
import { XRayContributions } from "./XRayContributions";
import clsx from "clsx";

type ViewMode = "table" | "contributions";

interface PortfolioXRayProps {
  portfolioId: string;
}

export function PortfolioXRay({ portfolioId }: PortfolioXRayProps) {
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const { showSuccess } = useNotification();

  const handleCopyToClipboard = () => {
    // This will be implemented by child components exposing their data
    const tableElement = document.querySelector("[data-xray-table]");
    if (tableElement) {
      // Get all rows including header
      const rows = tableElement.querySelectorAll("tr");
      const data: string[][] = [];

      rows.forEach((row) => {
        const cells = row.querySelectorAll("th, td");
        const rowData: string[] = [];
        cells.forEach((cell) => {
          rowData.push(cell.textContent?.trim() || "");
        });
        data.push(rowData);
      });

      // Convert to tab-delimited format for Excel
      const tsvContent = data.map((row) => row.join("\t")).join("\n");

      navigator.clipboard.writeText(tsvContent).then(() => {
        showSuccess("Copied to clipboard", "Paste into Excel or any spreadsheet");
      });
    }
  };

  return (
    <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-900">
      {/* Header with mode toggle and actions */}
      <div className="flex items-center justify-between px-6 py-4 bg-white dark:bg-slate-800 border-b border-slate-100 dark:border-slate-700">
        <div className="flex items-center gap-3">
          {/* Mode Toggle - Pill style like Meridian */}
          <div className="flex bg-slate-100 dark:bg-slate-700/50 rounded-full p-1">
            <button
              onClick={() => setViewMode("table")}
              className={clsx(
                "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
                viewMode === "table"
                  ? "bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              )}
            >
              <FontAwesomeIcon icon={faTable} className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Table View</span>
            </button>
            <button
              onClick={() => setViewMode("contributions")}
              className={clsx(
                "flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200",
                viewMode === "contributions"
                  ? "bg-white dark:bg-slate-600 text-slate-900 dark:text-white shadow-sm"
                  : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
              )}
            >
              <FontAwesomeIcon icon={faChartPie} className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Contributions</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          <Button
            color="white"
            onClick={handleCopyToClipboard}
            className="shadow-sm border border-slate-200 dark:border-slate-600"
            title="Copy to clipboard (paste in Excel)"
          >
            <FontAwesomeIcon icon={faCopy} className="mr-2 h-3.5 w-3.5" />
            <span className="hidden sm:inline">Copy to Excel</span>
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {viewMode === "table" ? (
          <XRayTableView portfolioId={portfolioId} />
        ) : (
          <XRayContributions portfolioId={portfolioId} />
        )}
      </div>
    </div>
  );
}

export default PortfolioXRay;
